import { useGmailThreadGet } from "@/components/EntitySelector/integrations/GmailSelector/useGmailThreadGet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useIntegrationConnection } from "@/hooks/useIntegrationConnection";
import { cn } from "@/utils";
import { GmailEmailEntity } from "@cohesive/api-entities";
import {
  ControlProps,
  optionIs,
  RankedTester,
  rankWith,
} from "@jsonforms/core";
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import { Loader2, X } from "lucide-react";
import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

interface EmailRecipientsData {
  to_recipients?: string[];
  cc_recipients?: string[];
  bcc_recipients?: string[];
  reply_all?: boolean;
}

type ReplyMode = "reply" | "reply_all";

const EmailRecipientsControlComponent: React.FC<ControlProps> = ({
  data,
  enabled,
  handleChange,
  path,
  visible,
  id,
}) => {
  const ctx = useJsonForms();
  const formData = ctx?.core?.data || {};

  // Get thread_id and platform from form data
  const threadId = formData?.thread_id as string | undefined;
  const platform = formData?.platform as string | undefined;

  // Get Gmail connection
  const gmailConnection = useIntegrationConnection("gmail");

  // Fetch thread data if we have the necessary info
  const { data: threadMessages, isLoading: isLoadingThread } =
    useGmailThreadGet(gmailConnection, { thread_id: threadId || "" });

  const [replyMode, setReplyMode] = useState<ReplyMode>("reply");
  const [showCc, setShowCc] = useState(
    () => data?.cc_recipients && data.cc_recipients.length > 0,
  );
  const [showBcc, setShowBcc] = useState(
    () => data?.bcc_recipients && data.bcc_recipients.length > 0,
  );
  const [toInput, setToInput] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");

  const recipientsData: EmailRecipientsData = data || {};
  const readonly = !enabled;

  // Check if we have Gmail thread data available
  // If platform is not specified but we have a thread_id and Gmail connection,
  // assume it's a Gmail thread (since we can only fetch Gmail threads with useGmailThreadGet)
  const isGmailThread =
    ((platform === "gmail" || !platform) && threadId && gmailConnection) ||
    false;

  // Get the last message from the thread
  const lastMessage = useMemo<GmailEmailEntity | null>(() => {
    if (!threadMessages?.data || threadMessages.data.length === 0) {
      return null;
    }
    return threadMessages.data[threadMessages.data.length - 1];
  }, [threadMessages]);

  // Calculate recipients based on reply mode
  const calculatedRecipients = useMemo(() => {
    if (!lastMessage?.data) return null;

    const sender = lastMessage.data.sender_address;
    const allRecipients = lastMessage.data.recipients || [];

    if (replyMode === "reply") {
      // Reply: only to the sender
      return {
        to_recipients: [sender],
        cc_recipients: undefined,
        bcc_recipients: undefined,
      };
    } else {
      // Reply All: sender + all original recipients (excluding ourselves)
      const toRecipients = allRecipients
        .filter((r) => r.type === "to")
        .map((r) => r.address);
      const ccRecipients = allRecipients
        .filter((r) => r.type === "cc")
        .map((r) => r.address);

      // Add sender to "to" recipients if not already there
      const finalToRecipients = [sender];
      toRecipients.forEach((addr) => {
        if (!finalToRecipients.includes(addr)) {
          finalToRecipients.push(addr);
        }
      });

      return {
        to_recipients: finalToRecipients,
        cc_recipients: ccRecipients.length > 0 ? ccRecipients : undefined,
        bcc_recipients: undefined,
      };
    }
  }, [lastMessage, replyMode]);

  // Auto-populate recipients when reply mode changes or thread loads initially
  useEffect(() => {
    if (calculatedRecipients && isGmailThread && !isLoadingThread) {
      // Always update when the calculated recipients change (from reply mode or initial load)
      // This allows the dropdown to control the recipients
      handleChange(path, {
        ...recipientsData,
        ...calculatedRecipients,
      });
      // Update show states for cc/bcc
      setShowCc(!!calculatedRecipients.cc_recipients);
      setShowBcc(!!calculatedRecipients.bcc_recipients);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatedRecipients, isGmailThread, isLoadingThread]);

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Add email to a field
  const addEmail = useCallback(
    (field: keyof EmailRecipientsData, email: string) => {
      const trimmedEmail = email.trim();
      if (!trimmedEmail || !isValidEmail(trimmedEmail)) return;

      const currentEmails = (recipientsData[field] as string[]) || [];
      if (currentEmails.includes(trimmedEmail)) return;

      handleChange(path, {
        ...recipientsData,
        [field]: [...currentEmails, trimmedEmail],
      });
    },
    [handleChange, path, recipientsData],
  );

  // Remove email from a field
  const removeEmail = useCallback(
    (field: keyof EmailRecipientsData, index: number) => {
      const currentEmails = (recipientsData[field] as string[]) || [];
      const newEmails = currentEmails.filter((_, i) => i !== index);

      handleChange(path, {
        ...recipientsData,
        [field]: newEmails.length > 0 ? newEmails : undefined,
      });
    },
    [handleChange, path, recipientsData],
  );

  // Handle keyboard input for adding emails
  const handleKeyDown = useCallback(
    (
      e: KeyboardEvent<HTMLInputElement>,
      field: keyof EmailRecipientsData,
      input: string,
      setInput: (value: string) => void,
    ) => {
      if (e.key === "Enter" || e.key === "," || e.key === " ") {
        e.preventDefault();
        addEmail(field, input);
        setInput("");
      } else if (e.key === "Backspace" && input === "") {
        // Remove last email on backspace if input is empty
        const currentEmails = (recipientsData[field] as string[]) || [];
        if (currentEmails.length > 0) {
          removeEmail(field, currentEmails.length - 1);
        }
      }
    },
    [addEmail, removeEmail, recipientsData],
  );

  // Handle input blur (add email when focus leaves)
  const handleBlur = useCallback(
    (
      field: keyof EmailRecipientsData,
      input: string,
      setInput: (value: string) => void,
    ) => {
      if (input.trim()) {
        addEmail(field, input);
        setInput("");
      }
    },
    [addEmail],
  );

  // Toggle reply_all
  const toggleReplyAll = useCallback(() => {
    handleChange(path, {
      ...recipientsData,
      reply_all: !recipientsData.reply_all,
    });
  }, [handleChange, path, recipientsData]);

  if (!visible) return null;

  // Show loading state while Gmail thread is being fetched
  const isLoadingGmailData = isGmailThread && isLoadingThread && !lastMessage;

  return (
    <div className="space-y-0 mb-4" id={id}>
      {/* To Recipients */}
      <h1 className="text-lg font-bold mb-4">Draft Email Reply</h1>
      <hr className="mb-4" />

      {/* Loading State for Gmail Thread */}
      {isLoadingGmailData && (
        <div className="mb-4 p-4 bg-muted/50 rounded-lg border border-muted">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <div className="flex-1 space-y-2">
              <p className="text-sm font-medium text-foreground">
                Loading email thread...
              </p>
              <p className="text-xs text-muted-foreground">
                Fetching recipients from the thread to populate reply options
              </p>
            </div>
          </div>
          <div className="mt-3 space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-8 w-3/4" />
          </div>
        </div>
      )}

      {/* Gmail Reply Mode Selector */}
      {isGmailThread && lastMessage && !readonly && !isLoadingGmailData && (
        <div className="mb-4">
          <Label className="text-sm font-medium mb-2 block">Reply Type</Label>
          <Select
            value={replyMode}
            onValueChange={(value) => setReplyMode(value as ReplyMode)}
            disabled={isLoadingThread}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select reply type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="reply">
                Reply
                <span className="text-xs text-muted-foreground ml-2">
                  (to sender only)
                </span>
              </SelectItem>
              <SelectItem value="reply_all">
                Reply All
                <span className="text-xs text-muted-foreground ml-2">
                  (to all recipients)
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {!isLoadingGmailData && (
        <>
          <h2 className="text-sm font-medium mb-2">Recipients</h2>
          <div className="flex items-start gap-2 py-2 border-b border-neutral-200 dark:border-neutral-800">
            <Label className="text-sm text-muted-foreground pt-1.5 w-12 shrink-0">
              To
            </Label>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap gap-1.5 items-center">
                {(recipientsData.to_recipients || []).map((email, index) => (
                  <EmailChip
                    key={`${email}-${index}`}
                    email={email}
                    onRemove={() => removeEmail("to_recipients", index)}
                    readonly={readonly || isLoadingGmailData}
                  />
                ))}
                {!readonly && !isLoadingGmailData && (
                  <Input
                    value={toInput}
                    onChange={(e) => setToInput(e.target.value)}
                    onKeyDown={(e) =>
                      handleKeyDown(e, "to_recipients", toInput, setToInput)
                    }
                    onBlur={() =>
                      handleBlur("to_recipients", toInput, setToInput)
                    }
                    placeholder={
                      recipientsData.to_recipients?.length
                        ? ""
                        : "Add recipients"
                    }
                    className="border-0 shadow-none focus-visible:ring-0 p-0 h-7 flex-1 min-w-[200px]"
                    disabled={isLoadingGmailData}
                  />
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {!showCc && !readonly && !isLoadingGmailData && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCc(true)}
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  Cc
                </Button>
              )}
              {!showBcc && !readonly && !isLoadingGmailData && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowBcc(true)}
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  Bcc
                </Button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Cc Recipients */}
      {showCc && !isLoadingGmailData && (
        <div className="flex items-start gap-2 py-2 border-b border-neutral-200 dark:border-neutral-800">
          <Label className="text-sm text-muted-foreground pt-1.5 w-12 shrink-0">
            Cc
          </Label>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap gap-1.5 items-center">
              {(recipientsData.cc_recipients || []).map((email, index) => (
                <EmailChip
                  key={`${email}-${index}`}
                  email={email}
                  onRemove={() => removeEmail("cc_recipients", index)}
                  readonly={readonly || isLoadingGmailData}
                />
              ))}
              {!readonly && !isLoadingGmailData && (
                <Input
                  value={ccInput}
                  onChange={(e) => setCcInput(e.target.value)}
                  onKeyDown={(e) =>
                    handleKeyDown(e, "cc_recipients", ccInput, setCcInput)
                  }
                  onBlur={() =>
                    handleBlur("cc_recipients", ccInput, setCcInput)
                  }
                  placeholder="Add Cc recipients"
                  className="border-0 shadow-none focus-visible:ring-0 p-0 h-7 flex-1 min-w-[200px] px-2"
                  disabled={isLoadingGmailData}
                />
              )}
            </div>
          </div>
          {!readonly && !isLoadingGmailData && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowCc(false);
                handleChange(path, {
                  ...recipientsData,
                  cc_recipients: undefined,
                });
              }}
              className="h-7 w-7 p-0 shrink-0 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}

      {/* Bcc Recipients */}
      {showBcc && !isLoadingGmailData && (
        <div className="flex items-start gap-2 py-2 border-b border-neutral-200 dark:border-neutral-800">
          <Label className="text-sm text-muted-foreground pt-1.5 w-12 shrink-0">
            Bcc
          </Label>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap gap-1.5 items-center">
              {(recipientsData.bcc_recipients || []).map((email, index) => (
                <EmailChip
                  key={`${email}-${index}`}
                  email={email}
                  onRemove={() => removeEmail("bcc_recipients", index)}
                  readonly={readonly || isLoadingGmailData}
                />
              ))}
              {!readonly && !isLoadingGmailData && (
                <Input
                  value={bccInput}
                  onChange={(e) => setBccInput(e.target.value)}
                  onKeyDown={(e) =>
                    handleKeyDown(e, "bcc_recipients", bccInput, setBccInput)
                  }
                  onBlur={() =>
                    handleBlur("bcc_recipients", bccInput, setBccInput)
                  }
                  placeholder="Add Bcc recipients"
                  className="border-0 shadow-none focus-visible:ring-0 p-0 h-7 flex-1 min-w-[200px]"
                  disabled={isLoadingGmailData}
                />
              )}
            </div>
          </div>
          {!readonly && !isLoadingGmailData && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowBcc(false);
                handleChange(path, {
                  ...recipientsData,
                  bcc_recipients: undefined,
                });
              }}
              className="h-7 w-7 p-0 shrink-0 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}

      {/* Reply All Checkbox */}
      {recipientsData.reply_all !== undefined && !isLoadingGmailData && (
        <div className="flex items-center gap-2 pt-2">
          <Checkbox
            id={`${id}-reply-all`}
            checked={recipientsData.reply_all}
            onCheckedChange={toggleReplyAll}
            disabled={readonly || isLoadingGmailData}
          />
          <Label
            htmlFor={`${id}-reply-all`}
            className="text-sm font-normal cursor-pointer"
          >
            Reply to all recipients
          </Label>
        </div>
      )}
    </div>
  );
};

// Email chip component
interface EmailChipProps {
  email: string;
  onRemove: () => void;
  readonly?: boolean;
}

const EmailChip: React.FC<EmailChipProps> = ({ email, onRemove, readonly }) => {
  return (
    <Badge
      variant="outline"
      className={cn(
        "pl-2.5 pr-1 py-0.5 gap-1 font-normal bg-neutral-100 dark:bg-neutral-800 border-neutral-300 dark:border-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-700",
        readonly && "pr-2.5",
      )}
    >
      <span className="text-xs">{email}</span>
      {!readonly && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="h-4 w-4 p-0 hover:bg-transparent hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </Badge>
  );
};

/**
 * Tester function that determines if this control should be used.
 * Matches when the UI schema has the option { control: "email_recipients" }
 *
 * This control manages multiple email recipient fields and reply_all setting:
 * - to_recipients (required)
 * - cc_recipients (optional, hidden by default)
 * - bcc_recipients (optional, hidden by default)
 * - reply_all (optional checkbox)
 *
 * To use this control, add the following to your UI schema:
 * {
 *   "type": "Control",
 *   "scope": "#/properties",
 *   "options": {
 *     "control": "email_recipients"
 *   }
 * }
 */
export const emailRecipientsControlTester: RankedTester = rankWith(
  5,
  optionIs("control", "email_recipients"),
);

export const EmailRecipientsControl = withJsonFormsControlProps(
  EmailRecipientsControlComponent,
);
